/** What is being signed: a loan agreement, or an MSA, which is signed against its application. */
export enum SigningSubjectKind {
  Loan = "loan",
  Application = "application",
}

export interface SigningSubject {
  kind: SigningSubjectKind;
  id: number;
}

/** The subject's sign-agreement route, e.g. `account/42/setup/sign-agreement`. */
export const signAgreementRoute = (subject: SigningSubject): string =>
  `${subject.kind === SigningSubjectKind.Loan ? "account" : "application"}/${subject.id}/setup/sign-agreement`;

/** DocuSign envelope status, as RYNO mirrors it from the provider. */
export enum EnvelopeStatus {
  Unknown = "unknown",
  Created = "created",
  Sent = "sent",
  Delivered = "delivered",
  Completed = "completed",
  Declined = "declined",
  Voided = "voided",
}

/** One recipient's status on an envelope. */
export enum EnvelopeRecipientStatus {
  Unknown = "unknown",
  Created = "created",
  Sent = "sent",
  Delivered = "delivered",
  Completed = "completed",
  Declined = "declined",
  AutoResponded = "autoresponded",
  AuthenticationFailed = "authenticationfailed",
}

/** The DocuSign ceremony this signer should be sent into. */
export interface SigningSessionData {
  /** Single-use and valid for minutes — navigate to it immediately, never store it. */
  signingUrl: string | null;
  envelopeId: number;
  docuSignEnvelopeId: string;
  envelopeStatus: EnvelopeStatus;
  recipientStatus: EnvelopeRecipientStatus;
  expiresOnUtc: string | null;
  /** Other signers still outstanding, by name. Empty once everyone has signed. */
  outstandingSignerNames: string[];
  /** The integration key `DocuSign.loadDocuSign()` takes. Null when `signingUrl` is null. */
  integrationKey: string | null;
  /** Realm-correct DocuSign.js bundle (`js-d` on demo, `js` in production). Null when `signingUrl` is. */
  signingBundleUrl: string | null;
}

/** Where this signer stands, as the return page polls it. */
export interface SigningStateData {
  /** Null when the subject is an MSA application. */
  loanId: number | null;
  applicationId: number;
  envelopeId: number;
  docuSignEnvelopeId: string;
  envelopeStatus: EnvelopeStatus;
  recipientStatus: EnvelopeRecipientStatus;
  signedOnUtc: string | null;
  declinedReason: string | null;
  expiresOnUtc: string | null;
  /** What RYNO has recorded against this signer. */
  signedDocumentCodes: string[];
  outstandingSignerNames: string[];
  /** The loan the signature applies to. Null until that loan exists. */
  signedLoanId: number | null;
}

/** What DocuSign reports when the ceremony ends. */
export interface DocuSignCeremonyEvent {
  sessionEndType?: DocuSignReturnEvent | string;
  type?: DocuSignReturnEvent | string;
  event?: DocuSignReturnEvent | string;
  returnUrl?: string;
}

export enum DocuSignReturnEvent {
  SigningComplete = "signing_complete",
  ViewingComplete = "viewing_complete",
  Decline = "decline",
  Cancel = "cancel",
  SessionTimeout = "session_timeout",
  TtlExpired = "ttl_expired",
  Exception = "exception",
}
